import { z } from 'zod';

/** Prisma cuid() formatı. `slot_inst_01_...` gibi anlam taşıyan string ID'ler
 *  bilinçli olarak terk edildi — parse edilebilir ID, parse edilecek demektir
 *  ve `split('_')` ile parse edilen ID er ya da geç bozulur. */
export const idSchema = z.string().min(1).max(64);

/** Duvar saati "HH:MM" (00:00 - 23:59). */
export const timeStringSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Saat formatı HH:MM olmalıdır');

/** Takvim günü "YYYY-MM-DD". */
export const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih formatı YYYY-MM-DD olmalıdır')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Geçersiz takvim tarihi');

export const dayOfWeekSchema = z.coerce.number().int().min(0).max(6);

/**
 * Yalnızca `https://` görsel adresi.
 *
 * `z.string().url()` YETMEZ: altında `new URL()` vardır ve o, `javascript:`,
 * `data:`, `file:` gibi şemaları da GEÇERLİ kabul eder. Bir yönetici (veya
 * yönetici hesabını ele geçiren biri) `avatarUrl` alanına `javascript:...`
 * yazabiliyordu; değer bugün yalnızca `<img src>` içinde kullanıldığı için
 * çalışmazdı, ama aynı alan yarın bir `<a href>` veya bir CSS `url()` içine
 * girdiğinde sessizce bir XSS'e dönüşürdü. Şemayı burada kısıtlamak, o
 * gelecekteki hatayı imkânsız kılar.
 *
 * `http:` de dışarıda: karışık içerik (mixed content) tarayıcıda engellenir ve
 * kullanıcıya sebebi anlaşılmayan bozuk bir görsel olarak görünür.
 *
 * ---------------------------------------------------------------------------
 * KENDİ MEDYA KÜTÜPHANEMİZ İSTİSNADIR
 *
 * Yüklenen görseller `/media/<yyyy-mm>/<uuid>.<uzantı>` biçiminde GÖRELİ bir
 * yolla servis ediliyor (bkz. `media.storage.ts`). Mutlak adres üretmek,
 * uygulamanın kendi dış adresini bilmesini gerektirirdi ve o adres değiştiği
 * gün veritabanındaki tüm kayıtlar bozulurdu.
 *
 * İzin DAR tutuluyor: yalnızca tam olarak bu kalıp. "Göreli yolları kabul et"
 * demek yetmezdi — `//kotu.example.com` tarayıcı için protokol-göreli bir
 * MUTLAK adrestir ve tam da bu alanın engellemeye çalıştığı şeydir.
 */
const MEDIA_PATH = /^\/media\/\d{4}-\d{2}\/[0-9a-f-]{36}\.[a-z0-9]{2,5}$/;

export const imageUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((value) => {
    if (MEDIA_PATH.test(value)) return true;
    try {
      return new URL(value).protocol === 'https:';
    } catch {
      return false;
    }
  }, 'Görsel adresi https:// ile başlamalı ya da medya kütüphanesinden seçilmelidir');

/**
 * Cursor tabanlı sayfalama.
 * Offset kullanılmadı: derin sayfalarda O(n) tarar ve araya yeni kayıt
 * girdiğinde sayfa kayarak kayıt atlar/tekrarlar.
 */
export const paginationQuerySchema = z.object({
  cursor: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Tüm API yanıtlarının tek zarfı. */
export type ApiSuccess<T> = { success: true; data: T };
export type ApiFailure = {
  success: false;
  error: { code: string; message: string; details?: unknown };
  requestId?: string;
};
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export const roleSchema = z.enum(['CLIENT', 'INSTRUCTOR', 'ADMIN']);
export type Role = z.infer<typeof roleSchema>;

export const sessionKindSchema = z.enum(['GROUP', 'PT']);
export type SessionKind = z.infer<typeof sessionKindSchema>;

export const creditKindSchema = z.enum(['GROUP', 'PT']);
export type CreditKind = z.infer<typeof creditKindSchema>;

export const bookingStatusSchema = z.enum([
  'CONFIRMED',
  'WAITLISTED',
  'CANCELLED',
  'ATTENDED',
  'NO_SHOW',
]);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

/* `ptSessionStatusSchema` KALDIRILDI. Yoklama artık program omurgasında
   yaşıyor: `attendanceStatusSchema` (`contracts/enrollment.ts`) — PRESENT /
   ABSENT / EXCUSED / LATE. */
